import { useState, useEffect, useRef } from "react";
import React from "react";
import axios from 'axios';
import { GrUserWorker } from "react-icons/gr";
import { MdCountertops } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import { RiImageAddFill } from "react-icons/ri";
import {
  Button,
  TextField,
  Grid,
  AppBar,
  Paper,
  Typography,
  IconButton,
  Avatar,
  MenuItem,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Select,
  CircularProgress,
  Box,
} from "@mui/material";
import {
  Person,
  Phone,
  Call,
  Room,
  Lock,
  AccountCircle,
  Public,
  Delete,
  Search,
  CalendarMonth as CalendarMonthIcon,
} from "@mui/icons-material";
import { BiSend } from "react-icons/bi";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

const WorkerManagement = () => {
  const [workerData, setWorkerData] = useState({
    name: "",
    workerNumber: "",
    counterNumber: "",
    phone: "",
    emergencyCall: "",
    address: "",
    country: "Others",
    username: "",
    password: "",
    image: "",
    imageName: "",
    joiningDate: "",
  });

  const [workers, setWorkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedDate, setSelectedDate] = useState("");
  const [nationalityFilter, setNationalityFilter] = useState("All");
  const [editIndex, setEditIndex] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedWorkerIndex, setSelectedWorkerIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceStats, setAttendanceStats] = useState({
    totalHours: 0,
    presentDays: 0,
    absentDays: 0,
    attendancePercent: 0,
  });

  const dateInputRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileOrTab = useMediaQuery(theme.breakpoints.down("md")); // true for md and below
  const printRef = useRef();

  useEffect(() => {
    fetchWorkers();
  }, []);

  useEffect(() => {
    if (workerData.name) {
      fetchAttendanceStats(workerData.name);
    }
  }, [workerData.name]);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://enzo-matr.onrender.com/api/workers/workers');
      setWorkers(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceStats = async (workerName) => {
    try {
      const res = await axios.get(`https://enzo-matr.onrender.com/attendances?worker=${encodeURIComponent(workerName)}`);
      const records = res.data;

      let totalHours = 0;
      let presentDays = 0;
      let absentDays = 0;

      records.forEach(record => {
        if (record.state === "PRESENT") presentDays++;
        if (record.state === "ABSENT") absentDays++;
        // Parse total as "8 30" (8 hours 30 mins)
        if (record.total) {
          const [h, m] = record.total.split(" ").map(Number);
          totalHours += h + (m ? m / 60 : 0);
        }
      });

      const totalDays = presentDays + absentDays;
      const attendancePercent = totalDays ? Math.round((presentDays / totalDays) * 100) : 0;

      setAttendanceStats({
        totalHours: totalHours.toFixed(2),
        presentDays,
        absentDays,
        attendancePercent,
      });
    } catch (err) {
      setAttendanceStats({
        totalHours: 0,
        presentDays: 0,
        absentDays: 0,
        attendancePercent: 0,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if ((name === "phone" || name === "emergencyCall") && (!/^\d*$/.test(value) || value.length > 10)) {
      return; // Restrict input to numbers only and limit to 10 digits
    }

    setWorkerData({ ...workerData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only check for duplicate phone, emergencyCall, and workerNumber
    const isDuplicate = workers.some(worker =>
      (worker.workerNumber === workerData.workerNumber && editIndex === null) ||
      (worker.phone === workerData.phone && editIndex === null) ||
      (worker.emergencyCall === workerData.emergencyCall && editIndex === null)
    );

    if (isDuplicate && editIndex === null) {
      alert('already data registered');
      return;
    }

    try {
      if (editIndex !== null) {
        await axios.put(`https://enzo-matr.onrender.com/api/workers/update-worker/${workers[editIndex]._id}`, workerData);
        fetchWorkers();
        setEditIndex(null);
        setOpenDialog(false);
        alert('Edited successfully!');
      } else {
        await axios.post('https://enzo-matr.onrender.com/api/workers/add-worker', workerData);
        fetchWorkers();
        alert('Worker saved successfully!');
      }
      setWorkerData({
        name: "",
        workerNumber: "",
        counterNumber: "",
        phone: "",
        emergencyCall: "",
        address: "",
        country: "Others",
        username: "",
        password: "",
        image: "",
        imageName: "",
        joiningDate: "",
      });
    } catch (err) {
      console.error(err);
      alert('Error saving worker');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setWorkerData({
          ...workerData,
          image: reader.result, // Store Base64 string
          imageName: file.name,
        });
      };
      reader.readAsDataURL(file); // Convert image to Base64
    }
  };

  const handleRemoveImage = () => {
    setWorkerData({ ...workerData, image: null, imageName: "" });
  };

  const handleDateChange = (event) => {
    const date = event.target.value;
    setWorkerData({ ...workerData, joiningDate: date });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  const handleNationalityFilter = (nationality) => {
    setNationalityFilter(nationality);
  };

  const handleViewAll = () => {
    setSearchTerm("");
    setSelectedMonth("All");
    setSelectedDate("");
    setNationalityFilter("All");
  };

  const handleEditWorker = (index) => {
    const worker = workers[index];
    setWorkerData({
      name: worker.name,
      workerNumber: worker.workerNumber,
      counterNumber: worker.counterNumber,
      phone: worker.phone,
      emergencyCall: worker.emergencyCall,
      address: worker.address,
      country: worker.country,
      username: worker.username,
      password: worker.password,
      image: worker.image,
      imageName: worker.image ? "image.png" : "",
      joiningDate: worker.joiningDate || "",
    });
    setEditIndex(index);
    setOpenDialog(true);
  };

  const handleDeleteWorker = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this worker?");
    if (!confirmDelete) return;
    try {
      await axios.delete(`https://enzo-matr.onrender.com/api/workers/delete-worker/${id}`);
      fetchWorkers();
      alert('Worker deleted successfully!');
    } catch (err) {
      console.error(err);
      alert('Error deleting worker');
    }
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
  };

  const filteredWorkers = workers.filter((worker) => {
    const matchesSearchTerm =
      (worker.name && worker.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (worker.username && worker.username.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesNationality =
      nationalityFilter === "All" || worker.country === nationalityFilter;

    const matchesMonth =
      selectedMonth === "All" ||
      (worker.joiningDate &&
        new Date(worker.joiningDate).toLocaleString("default", { month: "long" }) === selectedMonth);

    const matchesDate =
      !selectedDate ||
      (worker.joiningDate &&
        new Date(worker.joiningDate).toLocaleDateString() === new Date(selectedDate).toLocaleDateString());

    return matchesSearchTerm && matchesNationality && matchesMonth && matchesDate;
  });

  return (
    <>
      {/* Hidden printable profile */}
      <div style={{ display: "none" }}>
        <div ref={printRef}>
          <div style={{ padding: 32, fontFamily: "Arial", width: 400 }}>
            <img
              src={workerData.image}
              alt="worker"
              style={{ width: 120, height: 120, borderRadius: 12, marginBottom: 16, objectFit: "cover" }}
            />
            <h2>{workerData.name}</h2>
            <p><strong>Worker Number:</strong> {workerData.workerNumber}</p>
            <p><strong>Counter Number:</strong> {workerData.counterNumber}</p>
            <p><strong>Phone:</strong> {workerData.phone}</p>
            <p><strong>Emergency Call:</strong> {workerData.emergencyCall}</p>
            <p><strong>Address:</strong> {workerData.address}</p>
            <p><strong>Country:</strong> {workerData.country}</p>
            <p><strong>Username:</strong> {workerData.username}</p>
            <p><strong>Password:</strong> {workerData.password}</p>
            <p><strong>Joining Date:</strong> {workerData.joiningDate}</p>
          </div>
        </div>
      </div>
      <Paper style={{ fontFamily: "Arial", fontWeight: 'bold', overflow: 'hidden', height: '100vh', position: 'fixed', width: "100%" }}>
        <AppBar position="static" sx={{ backgroundColor: "#FFB400", boxShadow: "none" }}>
          <div style={{
            fontFamily: "caladea", width: "99.4%", background: "#fdb913", height: "30px", padding: 4,
            textAlign: "center", fontSize: "25px", color: "white", fontWeight: "bold"
          }}>
            ADD WORKERS
          </div>
        </AppBar>
        <Button
          size="small"
          variant="contained"
          sx={{
            backgroundColor: "#2f2f2f",
            color: "#fff",
            height: "34px",
            textTransform: "none",
            fontFamily: "caladea",
            fontSize: "20px",
            width: "98.0%",
            fontWeight: "bold",
            borderRadius: "10px",
            border: "1px solid black",
            "&:hover": { backgroundColor: "#2f2f2f" },
            margin: 1
          }}
          startIcon={<AddIcon sx={{ fontSize: 40 }} />}
          onClick={() => {
            setEditIndex(null); // Ensure it's add mode
            setWorkerData({
              name: "",
              workerNumber: "",
              counterNumber: "",
              phone: "",
              emergencyCall: "",
              address: "",
              country: "Others",
              username: "",
              password: "",
              image: "",
              imageName: "",
              joiningDate: "",
            });
            setOpenDialog(true);
          }}
        >
          Add New Profile
        </Button>
        {/* Responsive layout for worker detail and table */}
        {isMobileOrTab ? (
          // MOBILE & TAB: stack vertically
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%", mt: "8px" }}>
            {/* Worker Detail Section */}
            <Box sx={{ width: "100%", mb: 2 }}>
              {/* --- Copy the entire <Paper> for worker detail here --- */}
              <Paper
                elevation={3}
                sx={{
                  width: "90%",
                  p: 2,
                  mt: 0,
                  backgroundColor: "#f8f8f8",
                  position: "static",
                  margin: "auto",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderRadius: 3,
                  border: "2px solid #e0e0e0"
                }}
              >
                {/* Profile Image and Name Section */}
                <Avatar
                  src={workerData.image}
                  sx={{
                    width: 90,
                    height: 80,
                    borderRadius: 2,
                    mb: 2.3,
                    mt: -1,
                    boxShadow: 2,
                    marginLeft: -32,
                    bgcolor: "gray"
                  }}
                  variant="rounded"
                />
                <Typography sx={{
                  fontWeight: "bold",
                  fontSize: 22,
                  mt: -12,
                  textAlign: "center",
                  letterSpacing: 0.5
                }}>
                  {workerData.name
                    ? workerData.name.split(" ")[0].toUpperCase()
                    : "EMPLOYEE"}
                </Typography>
                <Typography sx={{ fontSize: 16, color: "#222", fontWeight: 500, mt: 0.5 }}>
                  {workerData.counterNumber ? workerData.counterNumber : "Counter No"}
                </Typography>
                <Typography sx={{ fontSize: 15, color: "#888", fontWeight: 500, mb: 1 }}>
                  {workerData.workerNumber ? workerData.workerNumber : "Employee ID"}
                </Typography>

                {/* Details Section */}
                <Paper
                  elevation={0}
                  sx={{
                    background: "#fff",
                    width: "100%",
                    p: 1.5,
                    mb: 1,
                    mt: -1,
                    borderRadius: 2,
                    boxShadow: "none",
                    border: "none"
                  }}
                >
                  <Typography sx={{ fontSize: 14, color: "#222", mb: 0.5 }}>
                    <strong>Employee Joining Date :</strong> {workerData.joiningDate}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222", mb: 0.5 }}>
                    <strong>Employee Phone Number :</strong> {workerData.phone}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222", mb: 0.5 }}>
                    <strong>Employee Emergency Number :</strong> {workerData.emergencyCall}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222", mb: 0.5 }}>
                    <strong>Employee Permanent Address :</strong> {workerData.address}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222", mt: 2 }}>
                    <strong>Employee Login User Name :</strong> {workerData.username}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222" }}>
                    <strong>Employee Login Password :</strong> {workerData.password}
                  </Typography>
                </Paper>

                {/* Total Working Hours */}
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: "#FFB400",
                    color: "#222",
                    fontWeight: "bold",
                    fontSize: 15,
                    borderRadius: 2,
                    mb: 1,
                    mt: -1.5,
                    boxShadow: "none",
                    border: "2px solid #e0e0e0",
                    letterSpacing: 0.5,
                    "&:hover": { background: "#FFB400" }
                  }}
                >
                  Total Working Hrs: {attendanceStats.totalHours} Hrs
                </Button>

                {/* Attendance/Absent Section */}
                <Grid container spacing={1} sx={{ mt: -1 }}>
                  <Grid item xs={6}>
                    <Paper
                      elevation={2}
                      sx={{
                        background: "#fff",
                        borderRadius: 2,
                        p: 0.5,
                        textAlign: "center",
                        border: "2px solid #e0e0e0"
                      }}
                    >
                      <Typography sx={{ fontWeight: "bold", fontSize: 20, color: "#222" }}>
                        {attendanceStats.attendancePercent}%
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#222", fontWeight: 500 }}>Attendance</Typography>
                      <Typography sx={{ fontSize: 12, color: "#888" }}>
                        {attendanceStats.presentDays} Days
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      elevation={2}
                      sx={{
                        background: "#111",
                        borderRadius: 2,
                        p: 0.5,
                        textAlign: "center",
                        color: "#fff",
                        border: "2px solid #e0e0e0"
                      }}
                    >
                      <Typography sx={{ fontWeight: "bold", fontSize: 20, color: "#FFB400" }}>
                        {attendanceStats.absentDays && attendanceStats.presentDays + attendanceStats.absentDays
                          ? Math.round((attendanceStats.absentDays / (attendanceStats.presentDays + attendanceStats.absentDays)) * 100)
                          : 0}%
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>Absent</Typography>
                      <Typography sx={{ fontSize: 12, color: "#fff" }}>
                        {attendanceStats.absentDays} Days
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Print Profile Button */}
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: "#FFB400",
                    color: "#222",
                    fontWeight: "bold",
                    fontSize: 20,
                    borderRadius: 2,
                    mt: 1,
                    boxShadow: "none",
                    border: "2px solid #e0e0e0",
                    letterSpacing: 0.5,
                    "&:hover": { background: "#FFB400" }
                  }}
                  onClick={handlePrint}
                >
                  PRINT PROFILE
                </Button>
              </Paper>
            </Box>
            {/* Table Section */}
            <Box sx={{ width: "97%" }}>
              <Card>
                <CardContent>
                  <Grid container spacing={1} alignItems="center" style={{ marginBottom: 10 }}>
                    <Grid item xs={3}>
                      <TextField
                        size="small"
                        placeholder="Search Worker name"
                        variant="outlined"
                        fullWidth
                        border='1px solid black'
                        fontFamily="caladea"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                          sx: { borderRadius: "10px", border: "1px solid" },
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <IconButton sx={{ backgroundColor: "#2f2f2f", fontFamily: "caladea", color: "#fff", borderRadius: "10px", width: 36, height: 36, border: "1px solid black" }}>
                        <Search />
                      </IconButton>
                    </Grid>
                    <Grid item xs={2}>
                      <Select
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        size="small"
                        fullWidth
                        style={{ display: 'none', backgroundColor: "#ffbb33", height: '39px', borderRadius: '10px', border: '1px solid black' }}
                      >
                        <MenuItem value="All">Month</MenuItem>
                        {[
                          "January", "February", "March", "April", "May", "June",
                          "July", "August", "September", "October", "November", "December"
                        ].map((month) => (
                          <MenuItem key={month} value={month}>{month}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={1.6}>
                      <Button variant="contained" fullWidth style={{ display: "none", backgroundColor: "#ffbb33", borderRadius: '10px', border: '1px solid black', color: '#2f2f2f' }}>
                        Date
                        <CalendarMonthIcon />
                        <input
                          type="date"
                          ref={dateInputRef}
                          style={{ width: "0px", height: "0px", backgroundColor: "#FFB400", border: "none" }}
                          onChange={handleDateChange}
                        />
                      </Button>
                    </Grid>
                    <Grid item xs={1.2}>
                      <Button
                        variant="contained"
                        fullWidth
                        style={{ backgroundColor: "#ffbb33", fontSize: '18px', textTransform: "none", fontWeight: 'bold', fontFamily: "caladea", color: "#000", borderRadius: '10px', border: '1px solid black' }}
                        onClick={() => handleNationalityFilter("Others")}
                      >
                        Others
                      </Button>
                    </Grid>
                    <Grid item xs={1.2}>
                      <Button
                        variant="contained"
                        fullWidth
                        style={{ backgroundColor: "#ffbb33", fontWeight: 'bold', fontSize: '18px', textTransform: "none", fontFamily: "caladea", color: "#000", borderRadius: '10px', border: '1px solid black' }}
                        onClick={() => handleNationalityFilter("Saudi")}
                      >
                        Saudi
                      </Button>
                    </Grid>
                    <Grid item xs={1.5}>
                      <Button
                        variant="contained"
                        style={{ backgroundColor: "#2f2f2f", width: isMobileOrTab? "130px":"0px",textTransform: "none", fontWeight: 'bold', fontSize: '18px', fontFamily: "caladea", color: "white", borderRadius: '10px', border: '1px solid black' }}
                        onClick={handleViewAll}>
                        View All
                      </Button>
                    </Grid>
                  </Grid>
                  <TableContainer
                    component={Paper}
                    style={{
                      height: 420,
                      overflow: "auto",
                      scrollbarWidth: "none",        // For Firefox
                      msOverflowStyle: "none"        // For IE and Edge
                    }}
                    sx={{
                      "&::-webkit-scrollbar": { display: "none" } // For Chrome, Safari, Opera
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {['Image', 'Name', 'Worker Detail', 'Phone No.', 'Emg No.', 'Worker Address', 'User Name', 'Password', 'Edit', 'Delete'].map((head) => (
                            <TableCell
                              key={head}
                              style={{
                                color: "#fff",
                                fontWeight: "bold",
                                backgroundColor: "#2f2f2f",
                                fontFamily: "Arial",
                                fontSize: "13px",
                                padding: "6px 8px",
                                height: "32px",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {head}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={10} align="center" sx={{ padding: "40px 0" }}>
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <CircularProgress
                                  size={40}
                                  thickness={5}
                                  sx={{
                                    color: "#FFB400",
                                    '& .MuiCircularProgress-circle': { stroke: "#000" }, // inner color
                                  }}
                                />
                                <Typography sx={{ mt: 1, color: "#FFB400", fontWeight: "bold" }}>
                                  Loading...
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredWorkers.map((worker, index) => (
                            <TableRow
                              key={index}
                              sx={{ height: "38px", cursor: "pointer" }}
                              selected={selectedWorkerIndex === index}
                              onClick={() => {
                                if (selectedWorkerIndex === index) {
                                  // Deselect if clicking the same row
                                  setSelectedWorkerIndex(null);
                                  setWorkerData({
                                    name: "",
                                    workerNumber: "",
                                    counterNumber: "",
                                    phone: "",
                                    emergencyCall: "",
                                    address: "",
                                    country: "Others",
                                    username: "",
                                    password: "",
                                    image: "",
                                    imageName: "",
                                    joiningDate: "",
                                  });
                                } else {
                                  setSelectedWorkerIndex(index);
                                  setWorkerData({
                                    name: worker.name,
                                    workerNumber: worker.workerNumber,
                                    counterNumber: worker.counterNumber,
                                    phone: worker.phone,
                                    emergencyCall: worker.emergencyCall,
                                    address: worker.address,
                                    country: worker.country,
                                    username: worker.username,
                                    password: worker.password,
                                    image: worker.image,
                                    imageName: worker.image ? "image.png" : "",
                                    joiningDate: worker.joiningDate || "",
                                  });
                                }
                              }}
                            >
                              <TableCell>
                                <img src={worker.image} alt="worker" style={{ borderRadius: "50%", width: "32px", height: "32px" }} />
                              </TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.name}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>
                                {worker.workerNumber}
                                <Typography variant="body2" style={{ color: "#888", fontWeight: 'bold', fontSize: "11px", lineHeight: 1 }}>
                                  {worker.counterNumber}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.phone}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.emergencyCall}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.address}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.username}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.password}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>
                                <IconButton
                                  sx={{
                                    bgcolor: "#fdb913",
                                    color: "white",
                                    borderRadius: "8px",
                                    border: "2px solid black",
                                    width: "28px",
                                    height: "28px",
                                    marginRight: "2px",
                                    "&:hover": { bgcolor: "#2f2f2f" },
                                    p: 0
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click
                                    handleEditWorker(index);
                                  }}
                                >
                                  <BiSend style={{ fontSize: "16px" }} />
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>
                                <IconButton
                                  sx={{
                                    bgcolor: "red",
                                    color: "white",
                                    borderRadius: "8px",
                                    border: "2px solid black",
                                    width: "28px",
                                    height: "28px",
                                    "&:hover": { bgcolor: "#2f2f2f" },
                                    p: 0
                                  }}
                                  onClick={() => handleDeleteWorker(worker._id)}
                                >
                                  <Delete style={{ fontSize: "16px" }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          </Box>
        ) : (
          // DESKTOP: keep original layout
          <Grid container spacing={0} sx={{ mt: "8px", mb: 0 }}>
            <Grid item xs={3} mt={-2}>
              <Paper
                elevation={3}
                sx={{
                  width: isSmallScreen ? "100%" : 350,
                  p: 2,
                  mt: -3,
                  backgroundColor: "#f8f8f8",
                  position: isSmallScreen ? "static" : "absolute",
                  left: 0,
                  margin: "10px",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  height: '70vh',
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderRadius: 3,
                  border: "2px solid #e0e0e0"
                }}
              >
                {/* Profile Image and Name Section */}
                <Avatar
                  src={workerData.image}
                  sx={{
                    width: 90,
                    height: 80,
                    borderRadius: 2,
                    mb: 2.3,
                    mt: -1,
                    boxShadow: 2,
                    marginLeft: -32,
                    bgcolor: "gray"
                  }}
                  variant="rounded"
                />
                <Typography sx={{
                  fontWeight: "bold",
                  fontSize: 22,
                  mt: -12,
                  textAlign: "center",
                  letterSpacing: 0.5
                }}>
                  {workerData.name
                    ? workerData.name.split(" ")[0].toUpperCase()
                    : "EMPLOYEE"}
                </Typography>
                <Typography sx={{ fontSize: 16, color: "#222", fontWeight: 500, mt: 0.5 }}>
                  {workerData.counterNumber ? workerData.counterNumber : "Counter No"}
                </Typography>
                <Typography sx={{ fontSize: 15, color: "#888", fontWeight: 500, mb: 1 }}>
                  {workerData.workerNumber ? workerData.workerNumber : "Employee ID"}
                </Typography>

                {/* Details Section */}
                <Paper
                  elevation={0}
                  sx={{
                    background: "#fff",
                    width: "100%",
                    p: 1.5,
                    mb: 1,
                    mt: -1,
                    borderRadius: 2,
                    boxShadow: "none",
                    border: "none"
                  }}
                >
                  <Typography sx={{ fontSize: 14, color: "#222", mb: 0.5 }}>
                    <strong>Employee Joining Date :</strong> {workerData.joiningDate}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222", mb: 0.5 }}>
                    <strong>Employee Phone Number :</strong> {workerData.phone}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222", mb: 0.5 }}>
                    <strong>Employee Emergency Number :</strong> {workerData.emergencyCall}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222", mb: 0.5 }}>
                    <strong>Employee Permanent Address :</strong> {workerData.address}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222", mt: 2 }}>
                    <strong>Employee Login User Name :</strong> {workerData.username}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#222" }}>
                    <strong>Employee Login Password :</strong> {workerData.password}
                  </Typography>
                </Paper>

                {/* Total Working Hours */}
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: "#FFB400",
                    color: "#222",
                    fontWeight: "bold",
                    fontSize: 15,
                    borderRadius: 2,
                    mb: 1,
                    mt: -1.5,
                    boxShadow: "none",
                    border: "2px solid #e0e0e0",
                    letterSpacing: 0.5,
                    "&:hover": { background: "#FFB400" }
                  }}
                >
                  Total Working Hrs: {attendanceStats.totalHours} Hrs
                </Button>

                {/* Attendance/Absent Section */}
                <Grid container spacing={1} sx={{ mt: -1 }}>
                  <Grid item xs={6}>
                    <Paper
                      elevation={2}
                      sx={{
                        background: "#fff",
                        borderRadius: 2,
                        p: 0.5,
                        textAlign: "center",
                        border: "2px solid #e0e0e0"
                      }}
                    >
                      <Typography sx={{ fontWeight: "bold", fontSize: 20, color: "#222" }}>
                        {attendanceStats.attendancePercent}%
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#222", fontWeight: 500 }}>Attendance</Typography>
                      <Typography sx={{ fontSize: 12, color: "#888" }}>
                        {attendanceStats.presentDays} Days
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      elevation={2}
                      sx={{
                        background: "#111",
                        borderRadius: 2,
                        p: 0.5,
                        textAlign: "center",
                        color: "#fff",
                        border: "2px solid #e0e0e0"
                      }}
                    >
                      <Typography sx={{ fontWeight: "bold", fontSize: 20, color: "#FFB400" }}>
                        {attendanceStats.absentDays && attendanceStats.presentDays + attendanceStats.absentDays
                          ? Math.round((attendanceStats.absentDays / (attendanceStats.presentDays + attendanceStats.absentDays)) * 100)
                          : 0}%
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>Absent</Typography>
                      <Typography sx={{ fontSize: 12, color: "#fff" }}>
                        {attendanceStats.absentDays} Days
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Print Profile Button */}
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: "#FFB400",
                    color: "#222",
                    fontWeight: "bold",
                    fontSize: 20,
                    borderRadius: 2,
                    mt: 1,
                    boxShadow: "none",
                    border: "2px solid #e0e0e0",
                    letterSpacing: 0.5,
                    "&:hover": { background: "#FFB400" }
                  }}
                  onClick={handlePrint}
                >
                  PRINT PROFILE
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={8.6} sx={{ ml: 50, mt: -1 }}>
              <Card>
                <CardContent>
                  <Grid container spacing={1} alignItems="center" style={{ marginBottom: 10 }}>
                    <Grid item xs={3}>
                      <TextField
                        size="small"
                        placeholder="Search Worker name"
                        variant="outlined"
                        fullWidth
                        border='1px solid black'
                        fontFamily="caladea"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                          sx: { borderRadius: "10px", border: "1px solid" },
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <IconButton sx={{ backgroundColor: "#2f2f2f", fontFamily: "caladea", color: "#fff", borderRadius: "10px", width: 36, height: 36, border: "1px solid black" }}>
                        <Search />
                      </IconButton>
                    </Grid>
                    <Grid item xs={2}>
                      <Select
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        size="small"
                        fullWidth
                        style={{ display: 'none', backgroundColor: "#ffbb33", height: '39px', borderRadius: '10px', border: '1px solid black' }}
                      >
                        <MenuItem value="All">Month</MenuItem>
                        {[
                          "January", "February", "March", "April", "May", "June",
                          "July", "August", "September", "October", "November", "December"
                        ].map((month) => (
                          <MenuItem key={month} value={month}>{month}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={1.6}>
                      <Button variant="contained" fullWidth style={{ display: "none", backgroundColor: "#ffbb33", borderRadius: '10px', border: '1px solid black', color: '#2f2f2f' }}>
                        Date
                        <CalendarMonthIcon />
                        <input
                          type="date"
                          ref={dateInputRef}
                          style={{ width: "0px", height: "0px", backgroundColor: "#FFB400", border: "none" }}
                          onChange={handleDateChange}
                        />
                      </Button>
                    </Grid>
                    <Grid item xs={1.4}>
                      <Button
                        variant="contained"
                        fullWidth
                        style={{ backgroundColor: "#ffbb33", fontSize: '18px', textTransform: "none", fontWeight: 'bold', fontFamily: "caladea", color: "#000", borderRadius: '10px', border: '1px solid black' }}
                        onClick={() => handleNationalityFilter("Others")}
                      >
                        Others
                      </Button>
                    </Grid>
                    <Grid item xs={1.4}>
                      <Button
                        variant="contained"
                        fullWidth
                        style={{ backgroundColor: "#ffbb33", fontWeight: 'bold', fontSize: '18px', textTransform: "none", fontFamily: "caladea", color: "#000", borderRadius: '10px', border: '1px solid black' }}
                        onClick={() => handleNationalityFilter("Saudi")}
                      >
                        Saudi
                      </Button>
                    </Grid>
                    <Grid item xs={1.9}>
                      <Button
                        variant="contained"
                        fullWidth
                        style={{ backgroundColor: "#2f2f2f", textTransform: "none", fontWeight: 'bold', fontSize: '18px', fontFamily: "caladea", color: "white", borderRadius: '10px', border: '1px solid black' }}
                        onClick={handleViewAll}>
                        View All
                      </Button>
                    </Grid>
                  </Grid>
                  <TableContainer
                    component={Paper}
                    style={{
                      height: 420,
                      overflow: "auto",
                      scrollbarWidth: "none",        // For Firefox
                      msOverflowStyle: "none"        // For IE and Edge
                    }}
                    sx={{
                      "&::-webkit-scrollbar": { display: "none" } // For Chrome, Safari, Opera
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {['Image', 'Name', 'Worker Detail', 'Phone No.', 'Emg No.', 'Worker Address', 'User Name', 'Password', 'Edit', 'Delete'].map((head) => (
                            <TableCell
                              key={head}
                              style={{
                                color: "#fff",
                                fontWeight: "bold",
                                backgroundColor: "#2f2f2f",
                                fontFamily: "Arial",
                                fontSize: "13px",
                                padding: "6px 8px",
                                height: "32px",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {head}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={10} align="center" sx={{ padding: "40px 0" }}>
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <CircularProgress
                                  size={40}
                                  thickness={5}
                                  sx={{
                                    color: "#FFB400",
                                    '& .MuiCircularProgress-circle': { stroke: "#000" }, // inner color
                                  }}
                                />
                                <Typography sx={{ mt: 1, color: "#FFB400", fontWeight: "bold" }}>
                                  Loading...
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredWorkers.map((worker, index) => (
                            <TableRow
                              key={index}
                              sx={{ height: "38px", cursor: "pointer" }}
                              selected={selectedWorkerIndex === index}
                              onClick={() => {
                                if (selectedWorkerIndex === index) {
                                  // Deselect if clicking the same row
                                  setSelectedWorkerIndex(null);
                                  setWorkerData({
                                    name: "",
                                    workerNumber: "",
                                    counterNumber: "",
                                    phone: "",
                                    emergencyCall: "",
                                    address: "",
                                    country: "Others",
                                    username: "",
                                    password: "",
                                    image: "",
                                    imageName: "",
                                    joiningDate: "",
                                  });
                                } else {
                                  setSelectedWorkerIndex(index);
                                  setWorkerData({
                                    name: worker.name,
                                    workerNumber: worker.workerNumber,
                                    counterNumber: worker.counterNumber,
                                    phone: worker.phone,
                                    emergencyCall: worker.emergencyCall,
                                    address: worker.address,
                                    country: worker.country,
                                    username: worker.username,
                                    password: worker.password,
                                    image: worker.image,
                                    imageName: worker.image ? "image.png" : "",
                                    joiningDate: worker.joiningDate || "",
                                  });
                                }
                              }}
                            >
                              <TableCell>
                                <img src={worker.image} alt="worker" style={{ borderRadius: "50%", width: "32px", height: "32px" }} />
                              </TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.name}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>
                                {worker.workerNumber}
                                <Typography variant="body2" style={{ color: "#888", fontWeight: 'bold', fontSize: "11px", lineHeight: 1 }}>
                                  {worker.counterNumber}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.phone}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.emergencyCall}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.address}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.username}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>{worker.password}</TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>
                                <IconButton
                                  sx={{
                                    bgcolor: "#fdb913",
                                    color: "white",
                                    borderRadius: "8px",
                                    border: "2px solid black",
                                    width: "28px",
                                    height: "28px",
                                    marginRight: "2px",
                                    "&:hover": { bgcolor: "#2f2f2f" },
                                    p: 0
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click
                                    handleEditWorker(index);
                                  }}
                                >
                                  <BiSend style={{ fontSize: "16px" }} />
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ fontFamily: "Arial", fontWeight: 'bold', fontSize: "13px", padding: "6px 8px" }}>
                                <IconButton
                                  sx={{
                                    bgcolor: "red",
                                    color: "white",
                                    borderRadius: "8px",
                                    border: "2px solid black",
                                    width: "28px",
                                    height: "28px",
                                    "&:hover": { bgcolor: "#2f2f2f" },
                                    p: 0
                                  }}
                                  onClick={() => handleDeleteWorker(worker._id)}
                                >
                                  <Delete style={{ fontSize: "16px" }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth PaperProps={{
          sx: {
            borderRadius: 3,
            border: '3px solid black',
            background: '#fff',
            boxShadow: 8,
          }
        }}>
          <DialogTitle
            sx={{
              background: "#fdb913",
              color: "#222",
              fontFamily: "caladea",
              fontWeight: "bold",
              fontSize: 24,
              textAlign: "center",
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
              p: 1.5,
              position: "relative"
            }}
          >
            {editIndex !== null ? "Update Worker Detail" : "Add Workers Detail"}
            <IconButton
              aria-label="close"
              onClick={() => setOpenDialog(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "#222",
                background: "#fff",
                border: "2px solid #222",
                borderRadius: "50%",
                width: 32,
                height: 32,
                "&:hover": { background: "#eee" }
              }}
              size="small"
            >
              <span style={{ fontWeight: "bold", fontSize: 18 }}>×</span>
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2 }}>
            <Paper
              elevation={0}
              sx={{
                background: "#fff",
                borderRadius: 2,
                boxShadow: "none",
                border: "none",
                p: 0,
              }}
            >
              <Grid container spacing={1.2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    name="name"
                    placeholder="Name"
                    value={workerData.name}
                    onChange={handleChange}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& input::placeholder": {
                        fontWeight: "bold",
                        fontSize: "1rem",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                      },
                      "& input": {
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                      },
                      mb: 1,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="joiningDate"
                    type="date"
                    value={workerData.joiningDate}
                    onChange={handleChange}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonthIcon sx={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="imageName"
                    placeholder="Add Image"
                    value={workerData.imageName}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton component="label" size="small">
                            <RiImageAddFill style={{ color: "gray", fontSize: 22 }} />
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleImageChange}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: workerData.image && (
                        <InputAdornment position="end">
                          <IconButton onClick={handleRemoveImage} size="small">
                            <Delete sx={{ color: "red" }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="workerNumber"
                    placeholder="Worker Number"
                    value={workerData.workerNumber}
                    onChange={handleChange}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <GrUserWorker style={{ color: "gray", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="counterNumber"
                    placeholder="Counter Number"
                    value={workerData.counterNumber}
                    onChange={handleChange}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdCountertops style={{ color: "gray", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="phone"
                    placeholder="Phone Number"
                    value={workerData.phone}
                    onChange={handleChange}
                    inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="emergencyCall"
                    placeholder="Emergency Call"
                    value={workerData.emergencyCall}
                    onChange={handleChange}
                    inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Call sx={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="address"
                    placeholder="Address"
                    value={workerData.address}
                    onChange={handleChange}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Room sx={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    name="country"
                    value={workerData.country}
                    onChange={handleChange}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Public sx={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  >
                    <MenuItem value="Others">Other</MenuItem>
                    <MenuItem value="Saudi">Saudi</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="username"
                    placeholder="User Name"
                    value={workerData.username}
                    onChange={handleChange}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountCircle sx={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="password"
                    placeholder="Password"
                    value={workerData.password}
                    onChange={handleChange}
                    InputProps={{
                      sx: { borderRadius: "10px", background: "#f8f8f8" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 1.5,
                      backgroundColor: "#111",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "1.3rem",
                      borderRadius: "8px",
                      height: 48,
                      fontFamily: "caladea",
                      letterSpacing: 1,
                      border: "2px solid #111",
                      boxShadow: "none",
                      "&:hover": { background: "#222" }
                    }}
                    onClick={handleSubmit}
                  >
                    {editIndex !== null ? "UPDATE WORKER" : "ADD WORKERS"}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </DialogContent>
        </Dialog>
      </Paper>
    </>
  );
};

export default WorkerManagement;